import React, { useMemo, useState } from "react";
import { ChevronDown, ImageIcon, Info, Ruler, Search } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { ItemDetailsDialog } from "../components/ItemDetailsDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fmt, mediaUrl } from "../lib/api";
import { UNIT_LABEL, itemQty, basisLabel } from "../lib/surfaces";

const WALL_MODES = [
  ["all", "Tutte le pareti"],
  ["1", "1 parete"],
  ["2", "2 pareti"],
  ["3", "3 pareti"],
  ["4", "4 pareti"],
  ["custom", "Superficie personalizzata"],
];

const COATING_HEIGHTS = [1.2, 1.5, 2, 2.7];

// basi non disponibili per il tipo di ambiente
const unavailable = (item, zone) => {
  const b = item.surface_basis;
  if (zone.kind === "external") return b === "walls" || b === "ceiling";
  return b === "vertical";
};

export const WorkPanel = ({ categories, items, zone, surfaces, selection, onToggle, onCfg }) => {
  const [open, setOpen] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [details, setDetails] = useState(null);
  const [query, setQuery] = useState("");
  const toggleCat = (k) => setOpen((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k]));

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      items.filter(
        (i) =>
          (i.active !== false || selection[i.id]) &&
          i.visible !== false &&
          !unavailable(i, zone) &&
          (!q ||
            i.name.toLowerCase().includes(q) ||
            (i.subcategory || "").toLowerCase().includes(q) ||
            (i.description || "").toLowerCase().includes(q))
      ),
    [items, zone, q, selection]
  );

  return (
    <div className="border border-border bg-card" data-testid="work-panel">
      <div className="border-b border-border px-5 py-4 hatch">
        <div className="label-xs text-muted-foreground">Lavorazioni · {zone.name}</div>
        <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
          Scegli gli interventi
        </h2>
        <p className="mt-1 label-xs text-muted-foreground">
          {zone.kind === "internal"
            ? `Pavimento ${surfaces.floor} m² · Soffitto ${surfaces.ceiling} m² · Pareti ${surfaces.walls} m² · Perimetro ${surfaces.perimeter} ml`
            : `Superficie ${surfaces.area} m² · Perimetro ${surfaces.perimeter} ml · Verticali ${surfaces.vertical} m²`}
        </p>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="work-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca una lavorazione (es. doccia, gres, pittura)"
            className="rounded-none pl-9 text-sm"
          />
        </div>
      </div>

      {categories.map((cat) => {
        const catItems = visible.filter((i) => i.category === cat.key);
        if (!catItems.length) return null;
        const isOpen = q ? true : open.includes(cat.key);
        const picked = catItems.filter((i) => selection[i.id]).length;
        const groups = [...new Set(catItems.map((i) => i.subcategory || ""))];
        return (
          <div key={cat.key} className="border-b border-border last:border-0">
            <button
              data-testid={`category-${cat.key}-toggle`}
              onClick={() => toggleCat(cat.key)}
              className="flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors hover:bg-[#F1F5F9]"
            >
              <span className="flex items-center gap-3">
                <ChevronDown
                  className={`h-4 w-4 text-[#4E7C59] transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`}
                />
                <span className="font-display text-base font-medium">{cat.name}</span>
                <span className="label-xs text-muted-foreground">{catItems.length}</span>
              </span>
              {picked > 0 && (
                <span className="font-mono-t text-xs text-[#4E7C59]">{picked} sel.</span>
              )}
            </button>

            {isOpen && (
              <div className="pb-2">
                {groups.map((group) => (
                  <div key={group || "_"}>
                    {group && (
                      <div className="bg-[#F3F6F1] px-5 py-1.5 label-xs text-[#4E7C59]">{group}</div>
                    )}
                    {catItems
                      .filter((i) => (i.subcategory || "") === group)
                      .map((item) => {
                        const cfg = selection[item.id];
                        const sel = !!cfg;
                        const qty = sel ? itemQty(item, surfaces, cfg) : 0;
                        const isWalls = item.surface_basis === "walls";
                        const isAuto = item.surface_basis !== "none";
                        return (
                          <div
                            key={item.id}
                            className={`px-5 py-3 transition-colors ${sel ? "bg-[#4E7C59]/[0.06]" : ""}`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                data-testid={`work-item-${item.id}-checkbox`}
                                checked={sel}
                                onCheckedChange={() => onToggle(item)}
                              />
                              {item.image_url ? (
                                <img
                                  data-testid={`work-item-${item.id}-thumb`}
                                  src={mediaUrl(item.image_url)}
                                  alt={item.name}
                                  loading="lazy"
                                  onClick={() => setZoom(item)}
                                  className="h-16 w-20 flex-none cursor-zoom-in border border-border object-cover transition-transform hover:scale-[1.03]"
                                />
                              ) : (
                                <span className="flex h-16 w-20 flex-none items-center justify-center border border-dashed border-border text-muted-foreground">
                                  <ImageIcon className="h-4 w-4" />
                                </span>
                              )}
                              <span
                                className="flex-1 cursor-pointer text-sm"
                                onClick={() => onToggle(item)}
                              >
                                <span className="flex flex-wrap items-baseline gap-2">
                                  <span>{item.name}</span>
                                  <span className="font-mono-t text-xs text-muted-foreground">
                                    € {fmt(item.price)}/{UNIT_LABEL[item.unit] || item.unit} + IVA
                                  </span>
                                  {item.active === false && (
                                    <span className="border border-border px-1.5 py-0.5 label-xs text-muted-foreground">
                                      non più in listino
                                    </span>
                                  )}
                                </span>
                                {item.description && (
                                  <span
                                    data-testid={`work-item-${item.id}-description`}
                                    className="mt-0.5 block text-xs leading-snug text-muted-foreground"
                                  >
                                    {item.description}
                                  </span>
                                )}
                                <span className="mt-1 flex flex-wrap items-center gap-3">
                                  {isAuto && (
                                    <span className="inline-flex items-center gap-1 label-xs text-[#4E7C59]">
                                      <Ruler className="h-3 w-3" /> {basisLabel(item.surface_basis)}
                                    </span>
                                  )}
                                  <span
                                    data-testid={`work-item-${item.id}-includes`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDetails(item);
                                    }}
                                    className="label-xs text-[#4E7C59] underline decoration-dotted"
                                  >
                                    Cosa comprende?
                                  </span>
                                </span>
                              </span>
                              <button
                                data-testid={`work-item-${item.id}-info`}
                                onClick={() => setDetails(item)}
                                className="flex-none text-muted-foreground transition-colors hover:text-[#4E7C59]"
                                aria-label={`Dettagli ${item.name}`}
                              >
                                <Info className="h-4 w-4" />
                              </button>
                              <span
                                data-testid={`work-item-${item.id}-total`}
                                className="w-24 flex-none text-right font-mono-t text-sm font-semibold"
                              >
                                {sel ? `€ ${fmt(qty * item.price)}` : "—"}
                              </span>
                            </div>

                            {sel && (
                              <div className="mt-3 flex flex-wrap items-end gap-3 border-l-2 border-[#4E7C59] pl-4">
                                {isWalls && (
                                  <>
                                    <label className="block">
                                      <span className="label-xs text-muted-foreground">
                                        Pareti interessate
                                      </span>
                                      <select
                                        data-testid={`work-item-${item.id}-wall-mode`}
                                        value={cfg.wallMode || "all"}
                                        onChange={(e) => onCfg(item.id, { wallMode: e.target.value })}
                                        className="mt-1 block border border-border bg-background px-2 py-1 text-sm outline-none focus:border-[#4E7C59]"
                                      >
                                        {WALL_MODES.map(([v, l]) => (
                                          <option key={v} value={v}>
                                            {l}
                                          </option>
                                        ))}
                                      </select>
                                    </label>

                                    {cfg.wallMode === "custom" ? (
                                      <label className="block">
                                        <span className="label-xs text-muted-foreground">
                                          Superficie
                                        </span>
                                        <input
                                          data-testid={`work-item-${item.id}-custom-area`}
                                          type="number"
                                          min="0"
                                          step="0.1"
                                          value={cfg.customArea ?? ""}
                                          onChange={(e) =>
                                            onCfg(item.id, { customArea: e.target.value })
                                          }
                                          className="mt-1 w-24 border border-border bg-background px-2 py-1 text-right font-mono-t text-sm outline-none focus:border-[#4E7C59]"
                                        />
                                      </label>
                                    ) : (
                                      <label className="block">
                                        <span className="label-xs text-muted-foreground">
                                          Altezza rivestimento
                                        </span>
                                        <span className="mt-1 flex items-center gap-2">
                                          <select
                                            data-testid={`work-item-${item.id}-coating-height`}
                                            value={
                                              COATING_HEIGHTS.includes(Number(cfg.wallHeight))
                                                ? String(cfg.wallHeight)
                                                : "custom"
                                            }
                                            onChange={(e) =>
                                              onCfg(item.id, {
                                                wallHeight:
                                                  e.target.value === "custom"
                                                    ? cfg.wallHeight
                                                    : Number(e.target.value),
                                              })
                                            }
                                            className="border border-border bg-background px-2 py-1 text-sm outline-none focus:border-[#4E7C59]"
                                          >
                                            {COATING_HEIGHTS.map((h) => (
                                              <option key={h} value={h}>
                                                {h.toString().replace(".", ",")} m
                                              </option>
                                            ))}
                                            <option value="custom">personalizzata</option>
                                          </select>
                                          <input
                                            data-testid={`work-item-${item.id}-wall-height`}
                                            type="number"
                                            min="0"
                                            step="0.05"
                                            value={cfg.wallHeight ?? surfaces.height}
                                            onChange={(e) =>
                                              onCfg(item.id, { wallHeight: e.target.value })
                                            }
                                            className="w-20 border border-border bg-background px-2 py-1 text-right font-mono-t text-sm outline-none focus:border-[#4E7C59]"
                                          />
                                        </span>
                                      </label>
                                    )}
                                  </>
                                )}

                                {!isAuto && (
                                  <label className="block">
                                    <span className="label-xs text-muted-foreground">
                                      Quantità ({UNIT_LABEL[item.unit] || item.unit})
                                    </span>
                                    <input
                                      data-testid={`work-item-${item.id}-qty-input`}
                                      type="number"
                                      min="0"
                                      step={
                                        item.unit === "m2" || item.unit === "ml" || item.unit === "m3"
                                          ? "0.5"
                                          : "1"
                                      }
                                      value={cfg.qty ?? 1}
                                      onChange={(e) => onCfg(item.id, { qty: e.target.value })}
                                      className="mt-1 w-24 border border-border bg-background px-2 py-1 text-right font-mono-t text-sm outline-none focus:border-[#4E7C59]"
                                    />
                                  </label>
                                )}

                                <span
                                  data-testid={`work-item-${item.id}-qty`}
                                  className="label-xs pb-1 text-muted-foreground"
                                >
                                  quantità calcolata:{" "}
                                  <strong className="font-mono-t text-foreground">
                                    {qty.toLocaleString("it-IT", { maximumFractionDigits: 2 })}{" "}
                                    {UNIT_LABEL[item.unit] || item.unit}
                                  </strong>
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <ItemDetailsDialog item={details} onClose={() => setDetails(null)} />

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent className="rounded-none sm:max-w-2xl" data-testid="image-lightbox">
          {zoom && (
            <div>
              <DialogHeader>
                <DialogTitle className="sr-only">{zoom.name}</DialogTitle>
              </DialogHeader>
              <img
                src={mediaUrl(zoom.image_url)}
                alt={zoom.name}
                className="max-h-[70vh] w-full object-contain"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="font-display text-base">{zoom.name}</span>
                <span className="font-mono-t text-sm text-[#4E7C59]">
                  € {fmt(zoom.price)}/{UNIT_LABEL[zoom.unit] || zoom.unit} + IVA
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
